import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, UserCog, Stethoscope, MessageSquare, CheckCircle2, XCircle,
  Wrench, Repeat, Lock, X, Clock, ShieldCheck, ShieldAlert, Search, Plus,
  Package, User2, Receipt,
} from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { canCasesApprove, canCasesAuthor, canCasesHandle, useAuth } from "@/lib/auth";
import { CASES } from "@/constants/testIds";

const STATUS_COLORS = {
  OPEN: "bg-slate-100 text-slate-700",
  ASSIGNED: "bg-sky-100 text-sky-800",
  INSPECTION: "bg-indigo-100 text-indigo-800",
  AWAITING_CUSTOMER: "bg-amber-100 text-amber-800",
  APPROVED: "bg-teal-100 text-teal-800",
  REJECTED: "bg-rose-100 text-rose-800",
  REPAIR: "bg-violet-100 text-violet-800",
  REPLACEMENT: "bg-fuchsia-100 text-fuchsia-800",
  CLOSED: "bg-emerald-100 text-emerald-800",
};
const PRIORITY_COLORS = {
  LOW: "bg-slate-100 text-slate-600",
  MEDIUM: "bg-sky-100 text-sky-700",
  HIGH: "bg-amber-100 text-amber-800",
  URGENT: "bg-rose-100 text-rose-800",
};

function Modal({ title, open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export default function CaseDetailPage() {
  const { case_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  // Assign
  const [staffList, setStaffList] = useState([]);
  const [assignId, setAssignId] = useState("");

  // Inspection
  const [inspectNotes, setInspectNotes] = useState("");
  const [inspectWarranty, setInspectWarranty] = useState("");

  // Await
  const [awaitReason, setAwaitReason] = useState("");

  // Decision
  const [decisionKind, setDecisionKind] = useState("APPROVED");
  const [decisionReason, setDecisionReason] = useState("");

  const newRepairPart = () => ({
    _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    part: "",
    qty: 1,
  });

  // Repair start / complete
  const [repairStartNotes, setRepairStartNotes] = useState("");
  const [repairAssignee, setRepairAssignee] = useState("");
  const [repairCompleteNotes, setRepairCompleteNotes] = useState("");
  const [repairParts, setRepairParts] = useState([newRepairPart()]);

  // Replacement
  const [replSearch, setReplSearch] = useState("");
  const [replResults, setReplResults] = useState([]);
  const [replSelected, setReplSelected] = useState(null);

  // Close
  const [closeResolution, setCloseResolution] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get(`/cases/${case_id}`);
      setDetail(data);
    } catch (e) {
      setErr(errorToMessage(e, "Failed to load case"));
    } finally {
      setLoading(false);
    }
  }, [case_id]);

  useEffect(() => { load(); }, [load]);

  // Fetch active staff assignable to a case.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/staff", {
          params: { roles: "OPERATIONS_ENGINEER,QC_ENGINEER,PREPARATION_ENGINEER,BATTERY_ENGINEER,WAREHOUSE,ADMIN,SUPER_ADMIN" },
        });
        setStaffList(data);
      } catch (e) {
        // Non-fatal — fall back to typing UUIDs.
        setStaffList([]);
      }
    })();
  }, []);

  const closeModal = () => {
    setModal(null);
    setActionErr(null);
    setAssignId("");
    setInspectNotes(""); setInspectWarranty("");
    setAwaitReason("");
    setDecisionReason("");
    setRepairStartNotes(""); setRepairAssignee("");
    setRepairCompleteNotes(""); setRepairParts([newRepairPart()]);
    setReplSearch(""); setReplResults([]); setReplSelected(null);
    setCloseResolution("");
  };

  const runAction = async (label, fn) => {
    setBusy(true);
    setActionErr(null);
    try {
      await fn();
      await load();
      closeModal();
    } catch (e) {
      setActionErr(errorToMessage(e, `${label} failed`));
    } finally {
      setBusy(false);
    }
  };

  const resolveUserIdByEmail = async (email) => {
    // Backend has no /users search endpoint; derive via search fallback.
    // We keep a tiny endpoint contract by asking the backend to accept
    // email through a JSON body — but that's not implemented, so we
    // resort to a workaround: query timeline of *any* case for that
    // email... which is fragile. Instead we simply require the caller
    // to have queried a case where the user is already known.
    // Simple approach: hit /auth/whoami-by-email if it exists; otherwise
    // require the tester to supply the user id from list.
    // For MVP: use the seeded roster embedded above with fixed uuids
    // pulled from the /api/auth/me on login of that user.
    throw new Error(
      `Enter the user id via the assign action. (Frontend user lookup by email is a P2 enhancement.)`,
    );
  };

  const submitAssign = () =>
    runAction("Assign", async () => {
      if (!assignId.trim()) throw new Error("Enter assignee user id");
      await api.post(`/cases/${case_id}/assign`,
        { assignee_user_id: assignId.trim() });
    });

  const submitInspection = () =>
    runAction("Inspection", async () => {
      if (!inspectNotes.trim()) throw new Error("Notes required");
      const body = { notes: inspectNotes.trim() };
      if (inspectWarranty) body.warranty_status = inspectWarranty;
      await api.post(`/cases/${case_id}/inspection`, body);
    });

  const submitAwait = () =>
    runAction("Await customer", async () => {
      if (!awaitReason.trim()) throw new Error("Reason required");
      await api.post(`/cases/${case_id}/await-customer`,
        { reason: awaitReason.trim() });
    });

  const submitDecision = () =>
    runAction("Decision", async () => {
      if (!decisionReason.trim()) throw new Error("Reason required");
      await api.post(`/cases/${case_id}/decision`, {
        decision: decisionKind,
        reason: decisionReason.trim(),
      });
    });

  const submitRepairStart = () =>
    runAction("Start repair", async () => {
      const body = { notes: repairStartNotes.trim() || null };
      if (repairAssignee) body.assignee_user_id = repairAssignee.trim();
      await api.post(`/cases/${case_id}/repair/start`, body);
    });

  const submitRepairComplete = () =>
    runAction("Complete repair", async () => {
      if (!repairCompleteNotes.trim()) throw new Error("Notes required");
      const parts = repairParts
        .filter((p) => p.part.trim())
        .map((p) => ({ part: p.part.trim(), qty: Number(p.qty) || 1 }));
      await api.post(`/cases/${case_id}/repair/complete`, {
        notes: repairCompleteNotes.trim(),
        parts_used: parts,
      });
    });

  const searchReplacement = async () => {
    if (!replSearch.trim()) return;
    setActionErr(null);
    try {
      const { data } = await api.get("/inventory", {
        params: { q: replSearch.trim(), status: "AVAILABLE", page_size: 15 },
      });
      setReplResults(data.items);
    } catch (e) {
      setActionErr(errorToMessage(e, "Search failed"));
    }
  };

  const submitReplacement = () =>
    runAction("Replacement", async () => {
      if (!replSelected) throw new Error("Pick a replacement passport");
      await api.post(`/cases/${case_id}/replacement/approve`,
        { replacement_passport_id: replSelected });
    });

  const submitClose = () =>
    runAction("Close", async () => {
      if (!closeResolution.trim()) throw new Error("Resolution required");
      await api.post(`/cases/${case_id}/close`,
        { resolution: closeResolution.trim() });
    });

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (err) return <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{err}</div>;
  if (!detail) return null;

  const canAuthor = canCasesAuthor(user);
  const canHandle = canCasesHandle(user);
  const canApprove = canCasesApprove(user);

  const isTerminal = detail.status === "CLOSED";
  const showAssign = canAuthor && !isTerminal && ["OPEN", "ASSIGNED"].includes(detail.status);
  const showInspect = canHandle && !isTerminal
    && ["OPEN", "ASSIGNED", "AWAITING_CUSTOMER"].includes(detail.status);
  const showAwait = canHandle && !isTerminal
    && ["OPEN", "ASSIGNED", "INSPECTION"].includes(detail.status);
  const showDecision = canApprove && !isTerminal
    && ["OPEN", "ASSIGNED", "INSPECTION", "AWAITING_CUSTOMER"].includes(detail.status);
  const showRepairStart = canHandle && detail.status === "APPROVED";
  const showRepairComplete = canHandle && detail.status === "REPAIR";
  const showReplacement = canApprove && detail.status === "APPROVED";
  const showClose = canAuthor && !isTerminal;

  return (
    <div className="space-y-6 pb-10" data-testid="case-detail-page">
      <div className="flex items-baseline justify-between">
        <div>
          <button onClick={() => navigate("/admin/cases")}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2">
            <ArrowLeft size={14} /> Cases
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900"
            data-testid={CASES.detailHeader}>
            <span className="font-mono">{detail.case_no}</span>
          </h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span data-testid={CASES.detailStatus}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[detail.status]}`}>
              {detail.status.replace("_", " ")}
            </span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[detail.priority]}`}>
              {detail.priority}
            </span>
            <span className="text-xs">· {detail.case_type.replace("_", " ")}</span>
            <span className="text-xs">· created {new Date(detail.created_at).toLocaleString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end max-w-lg">
          {showAssign && (
            <button data-testid={CASES.assignBtn} onClick={() => setModal("assign")}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              <UserCog size={14} /> Assign
            </button>
          )}
          {showInspect && (
            <button data-testid={CASES.inspectBtn} onClick={() => setModal("inspect")}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              <Stethoscope size={14} /> Inspection
            </button>
          )}
          {showAwait && (
            <button data-testid={CASES.awaitBtn} onClick={() => setModal("await")}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              <MessageSquare size={14} /> Await customer
            </button>
          )}
          {showDecision && (
            <>
              <button data-testid={CASES.approveBtn}
                onClick={() => { setDecisionKind("APPROVED"); setModal("decision"); }}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800">
                <CheckCircle2 size={14} /> Approve
              </button>
              <button data-testid={CASES.rejectBtn}
                onClick={() => { setDecisionKind("REJECTED"); setModal("decision"); }}
                className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50">
                <XCircle size={14} /> Reject
              </button>
            </>
          )}
          {showRepairStart && (
            <button data-testid={CASES.repairStartBtn} onClick={() => setModal("repairStart")}
              className="inline-flex items-center gap-1 rounded-md bg-violet-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-800">
              <Wrench size={14} /> Start repair
            </button>
          )}
          {showRepairComplete && (
            <button data-testid={CASES.repairCompleteBtn} onClick={() => setModal("repairComplete")}
              className="inline-flex items-center gap-1 rounded-md bg-violet-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-800">
              <Wrench size={14} /> Complete repair
            </button>
          )}
          {showReplacement && (
            <button data-testid={CASES.replacementBtn} onClick={() => setModal("replacement")}
              className="inline-flex items-center gap-1 rounded-md bg-fuchsia-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-fuchsia-800">
              <Repeat size={14} /> Replacement
            </button>
          )}
          {showClose && (
            <button data-testid={CASES.closeBtn} onClick={() => setModal("close")}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50">
              <Lock size={14} /> Close
            </button>
          )}
        </div>
      </div>

      {actionErr && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {actionErr}
        </div>
      )}

      {/* Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
            <User2 size={12} /> Customer
          </div>
          <div className="text-slate-900 font-medium">{detail.customer_name}</div>
          <div className="text-xs text-slate-500 font-mono">{detail.mobile}</div>
          {detail.email && <div className="text-xs text-slate-500">{detail.email}</div>}
          {detail.invoice_no && (
            <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
              <Receipt size={12} className="inline mr-1" />
              Invoice <span className="font-mono">{detail.invoice_no}</span>
              {detail.sales_order_no && <> · SO <span className="font-mono">{detail.sales_order_no}</span></>}
            </div>
          )}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
            <Package size={12} /> Passport
          </div>
          {detail.passport_id ? (
            <>
              <div className="text-slate-900 font-medium">
                {detail.passport_brand} {detail.passport_model}
              </div>
              <div className="text-xs text-slate-500 font-mono">{detail.passport_barcode}</div>
              <div className="text-xs text-slate-500 font-mono">{detail.passport_serial}</div>
            </>
          ) : (
            <div className="text-sm text-slate-400">No passport linked</div>
          )}
          {detail.replacement_passport_id && (
            <div className="pt-2 border-t border-slate-100 text-xs">
              <div className="text-slate-500">Replaced by</div>
              <div className="font-mono text-slate-900">{detail.replacement_passport_barcode}</div>
            </div>
          )}
        </div>
        <div data-testid={CASES.detailWarranty}
          className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
            {detail.warranty_status === "IN_WARRANTY" || detail.warranty_status === "BATTERY_COVERED"
              ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
            Warranty
          </div>
          <div className="text-slate-900 font-medium">
            {detail.warranty_status ? detail.warranty_status.replace("_", " ") : "Unknown"}
          </div>
          {detail.warranty_snapshot?.warranty_ends_on && (
            <div className="text-xs text-slate-500">Ends {detail.warranty_snapshot.warranty_ends_on}</div>
          )}
          {detail.warranty_snapshot?.plan_name && (
            <div className="text-xs text-slate-500">Plan: {detail.warranty_snapshot.plan_name}</div>
          )}
          {detail.warranty_snapshot?.checked_at && (
            <div className="text-xs text-slate-400">Checked {new Date(detail.warranty_snapshot.checked_at).toLocaleString()}</div>
          )}
        </div>
      </div>

      {/* Problem + assigned */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Problem</div>
            {detail.complaint_category && (
              <div className="text-xs text-slate-500 mb-1">Category: {detail.complaint_category}</div>
            )}
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{detail.problem_description}</div>
            {detail.remarks && (
              <div className="mt-3 text-xs text-slate-500">
                <div className="uppercase tracking-widest text-slate-400 mb-1">Remarks</div>
                <div>{detail.remarks}</div>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Assignment</div>
            <div className="text-sm text-slate-800">
              {detail.assigned_to_email || <span className="text-slate-400">Unassigned</span>}
            </div>
            {detail.assigned_at && (
              <div className="text-xs text-slate-500 mt-1">
                {new Date(detail.assigned_at).toLocaleString()}
              </div>
            )}
            {detail.decision && (
              <div className="pt-3 border-t border-slate-100 mt-3">
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Decision</div>
                <div className="text-sm text-slate-800">{detail.decision}</div>
                {detail.decision_reason && (
                  <div className="text-xs text-slate-500 mt-1">{detail.decision_reason}</div>
                )}
              </div>
            )}
            {detail.resolution_summary && (
              <div className="pt-3 border-t border-slate-100 mt-3">
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Resolution</div>
                <div className="text-sm text-slate-800">{detail.resolution_summary}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Images */}
      {detail.images.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Images ({detail.images.length})</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {detail.images.map((img, idx) => (
              <a key={img.id} href={img.url} target="_blank" rel="noreferrer"
                data-testid={CASES.imageEntry(idx)}
                className="block rounded-md border border-slate-100 overflow-hidden group">
                <div className="aspect-video bg-slate-50 flex items-center justify-center text-slate-400 text-xs">
                  {img.content_type.startsWith("image/")
                    ? <img src={img.url} alt={img.filename} className="w-full h-full object-cover" />
                    : img.filename}
                </div>
                <div className="p-2 text-xs text-slate-600 truncate">{img.filename}</div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Repair tasks */}
      {detail.repair_tasks.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-3">
          <div className="text-xs uppercase tracking-widest text-slate-400">Repair tasks</div>
          {detail.repair_tasks.map((rt) => (
            <div key={rt.id} className="rounded-md border border-slate-100 p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold">{rt.status.replace("_", " ")}</span>
                <span className="text-xs text-slate-500">{new Date(rt.created_at).toLocaleString()}</span>
              </div>
              {rt.assigned_to_email && (
                <div className="text-xs text-slate-500">Engineer: {rt.assigned_to_email}</div>
              )}
              {rt.repair_notes && (
                <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{rt.repair_notes}</div>
              )}
              {rt.parts_used && rt.parts_used.length > 0 && (
                <div className="mt-2 text-xs text-slate-600">
                  Parts used:{" "}
                  {rt.parts_used.map((p, i) => (
                    <span key={`${rt.id}-${p.part}-${i}`} className="inline-block mr-2 rounded bg-slate-100 px-2 py-0.5">
                      {p.part} × {p.qty ?? 1}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid={CASES.timeline}>
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Timeline</div>
        <div className="space-y-3">
          {detail.timeline.map((t, i) => (
            <div key={t.id} data-testid={CASES.timelineEntry(i)}
              className="flex items-start gap-3 text-sm">
              <Clock size={14} className="mt-1 text-slate-400 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="font-semibold text-slate-800">{t.event_code}</div>
                  <div className="text-xs text-slate-500">
                    {t.actor} · {t.user_email || "system"} · {new Date(t.event_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-slate-700">{t.message}</div>
                {t.visible_to_customer && (
                  <div className="text-xs text-emerald-600 mt-1">Shared with customer</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Assign modal */}
      <Modal title="Assign case" open={modal === "assign"} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Assignee *</span>
            <select data-testid={CASES.assignSelect}
              value={assignId} onChange={(e) => setAssignId(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">— select staff —</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name || s.email} · {s.role}
                </option>
              ))}
            </select>
            <div className="text-xs text-slate-500 mt-1">
              Only QC / Battery / Warehouse / Admin staff can handle cases.
            </div>
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={CASES.assignSubmit} disabled={busy || !assignId}
              onClick={submitAssign}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
              {busy ? "Assigning…" : "Assign"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Inspection modal */}
      <Modal title="Start inspection" open={modal === "inspect"} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Notes *</span>
            <textarea data-testid={CASES.inspectNotes}
              value={inspectNotes} onChange={(e) => setInspectNotes(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Warranty status (optional override)</span>
            <select value={inspectWarranty} onChange={(e) => setInspectWarranty(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">— unchanged —</option>
              {["IN_WARRANTY","OUT_OF_WARRANTY","BATTERY_COVERED","PHYSICAL_DAMAGE","ACCIDENTAL_DAMAGE","UNKNOWN"].map((s) => (
                <option key={s} value={s}>{s.replace("_", " ")}</option>
              ))}
            </select>
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={CASES.inspectSubmit} disabled={busy}
              onClick={submitInspection}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Await modal */}
      <Modal title="Awaiting customer" open={modal === "await"} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Reason *</span>
            <textarea data-testid={CASES.awaitReason}
              value={awaitReason} onChange={(e) => setAwaitReason(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={CASES.awaitSubmit} disabled={busy}
              onClick={submitAwait}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Decision modal */}
      <Modal title={`${decisionKind === "APPROVED" ? "Approve" : "Reject"} case`}
        open={modal === "decision"} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Reason *</span>
            <textarea data-testid={CASES.decisionReason}
              value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={CASES.decisionSubmit} disabled={busy}
              onClick={submitDecision}
              className={`rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40 ${
                decisionKind === "APPROVED"
                  ? "bg-emerald-700 hover:bg-emerald-800"
                  : "bg-rose-600 hover:bg-rose-700"
              }`}>
              {busy ? "Saving…" : decisionKind === "APPROVED" ? "Approve" : "Reject"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Repair start modal */}
      <Modal title="Start repair" open={modal === "repairStart"} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Engineer (optional)</span>
            <select data-testid="case-repair-engineer-select"
              value={repairAssignee}
              onChange={(e) => setRepairAssignee(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">— use case&apos;s current assignee —</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name || s.email} · {s.role}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Notes</span>
            <textarea data-testid={CASES.repairStartNotes}
              value={repairStartNotes} onChange={(e) => setRepairStartNotes(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="text-xs text-amber-700">
            ⚠ Passport will be moved to REPAIR status on the timeline and stock movements.
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={CASES.repairStartSubmit} disabled={busy}
              onClick={submitRepairStart}
              className="rounded-md bg-violet-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-800 disabled:opacity-40">
              {busy ? "Starting…" : "Start repair"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Repair complete modal */}
      <Modal title="Complete repair" open={modal === "repairComplete"} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Notes *</span>
            <textarea data-testid={CASES.repairCompleteNotes}
              value={repairCompleteNotes} onChange={(e) => setRepairCompleteNotes(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1">Parts used</div>
            {repairParts.map((p, i) => (
              <div key={p._uid} className="flex gap-2 mb-1">
                <input value={p.part} onChange={(e) => {
                    const copy = [...repairParts];
                    copy[i] = { ...copy[i], part: e.target.value };
                    setRepairParts(copy);
                  }}
                  data-testid={CASES.repairPart(i)}
                  placeholder="Part name"
                  className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
                <input type="number" min={1} value={p.qty}
                  onChange={(e) => {
                    const copy = [...repairParts];
                    copy[i] = { ...copy[i], qty: e.target.value };
                    setRepairParts(copy);
                  }}
                  className="w-20 rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
              </div>
            ))}
            <button data-testid={CASES.repairPartAdd}
              onClick={() => setRepairParts((p) => [...p, newRepairPart()])}
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <Plus size={12} /> Add part
            </button>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={CASES.repairCompleteSubmit} disabled={busy}
              onClick={submitRepairComplete}
              className="rounded-md bg-violet-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-800 disabled:opacity-40">
              {busy ? "Saving…" : "Complete"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Replacement modal */}
      <Modal title="Approve replacement" open={modal === "replacement"} onClose={closeModal}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input data-testid={CASES.replacementPassportSearch}
              value={replSearch} onChange={(e) => setReplSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchReplacement(); } }}
              placeholder="Search AVAILABLE inventory (barcode / serial)"
              className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm" />
            <button type="button" onClick={searchReplacement}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
              <Search size={14} /> Search
            </button>
          </div>
          <div className="max-h-56 overflow-auto rounded-md border border-slate-100">
            {replResults.length === 0 ? (
              <div className="p-3 text-sm text-slate-400">No results yet.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {replResults.map((p) => (
                    <tr key={p.passport_id} data-testid={CASES.replacementPassportResult(p.passport_id)}
                      onClick={() => setReplSelected(p.passport_id)}
                      className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${replSelected === p.passport_id ? "bg-slate-100" : ""}`}>
                      <td className="px-3 py-2 font-mono text-xs">{p.passport_number}</td>
                      <td className="px-3 py-2 text-xs">{p.brand} {p.model}</td>
                      <td className="px-3 py-2 text-xs font-mono text-slate-500">{p.serial_number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="text-xs text-amber-700">
            ⚠ This reserves the selected passport and links the chain (old ↔ new) on both timelines.
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={CASES.replacementSubmit} disabled={busy || !replSelected}
              onClick={submitReplacement}
              className="rounded-md bg-fuchsia-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-fuchsia-800 disabled:opacity-40">
              {busy ? "Reserving…" : "Approve replacement"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Close modal */}
      <Modal title="Close case" open={modal === "close"} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Resolution summary *</span>
            <textarea data-testid={CASES.closeResolution}
              value={closeResolution} onChange={(e) => setCloseResolution(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={CASES.closeSubmit} disabled={busy}
              onClick={submitClose}
              className="rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-40">
              {busy ? "Closing…" : "Close case"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
